export const calculatePercentage = (part: number, total: number, decimals = 1): number => {
  if (total === 0) return 0;
  return Number(((part / total) * 100).toFixed(decimals));
};

export const formatDecimal = (value: number, decimals: number): string =>
  value.toLocaleString("es-CO", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

export const formatInteger = (value: number): string => value.toLocaleString("es-CO");

// 12240 → "3h 24m"; 300 → "5m"; 0 → "0m"
export const formatDurationHM = (seconds: number): string => {
  const totalMinutes = Math.round(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  return `${hours}h ${minutes}m`;
};

export const transformDataForRecharts = (data: { [key: string]: number }) => {
  return Object.entries(data)
    .filter(
      ([key, value]) =>
        value !== undefined && value !== null && key !== "undefined" && key !== "N/A"
    )
    .map(([key, value]) => ({
      name: key,
      value,
    }));
};

export const getBadgeColor = (status: string) => {
  // Map colors
  switch (status) {
    // new stage
    case "new":
    case "delivered":
    case "read":
    case "answered":
      return "#FF8C00"; // Orange
    // pending stage
    case "pending":
    case "redirected":
    case "appointmentIntent":
    case "appointmentPending":
      return "#BA8E23"; // Yellow
    // success stage
    case "success":
    case "closed":
    case "appointmentConfirmed":
      return "#3CB18B"; // Success green
    // failure stage
    case "failure":
    case "inactive":
    case "appointmentCancelled":
    case "discarded":
      return "#EC5858"; // Red
    // ai_agent stage
    case "ai_agent":
      return "#5B2E9D"; // Purple text
    // customer stage
    case "customer":
      return "#2E5B9D"; // Blue text
    default:
      return "gray.500";
  }
};

export const getBadgeColorByStage = (stage: string) => {
  switch (stage) {
    case "new":
      return "#FF8C00"; // Orange
    case "pending":
      return "#BA8E23"; // Yellow
    case "success":
      return "#3CB18B"; // Success green
    case "failure":
      return "#EC5858"; // Red
    default:
      return "gray.500";
  }
};

export const getBadgeBgColor = (status: string) => {
  // Map background colors
  switch (status) {
    // new stage
    case "new":
    case "delivered":
    case "read":
    case "answered":
      return "#FFF0E0"; // Light orange
    // pending stage
    case "pending":
    case "redirected":
    case "appointmentIntent":
    case "appointmentPending":
      return "#FFF8DC"; // Light yellow
    // success stage
    case "success":
    case "closed":
    case "appointmentConfirmed":
      return "#E3FFE4"; // Light green
    // failure stage
    case "failure":
    case "inactive":
    case "appointmentCancelled":
    case "discarded":
      return "#FFDCDC"; // Light red
    // ai_agent stage
    case "ai_agent":
      return "#F2EBFF"; // Light purple background
    // customer stage
    case "customer":
      return "#EBF2FF"; // Light blue background

    default:
      return "gray.100";
  }
};

export const getBadgeBgColorByStage = (stage: string) => {
  switch (stage) {
    case "new":
      return "#FFF0E0"; // Light orange
    case "pending":
      return "#FFF8DC"; // Light yellow
    case "success":
      return "#E3FFE4"; // Light green
    case "failure":
      return "#FFDCDC"; // Light red
    default:
      return "gray.100";
  }
};

// Helper functions to get colors by pipeline position ID
export const getBadgeColorByPipelinePosition = (
  pipelinePositionId: string,
  allPositions: Array<{ id: string; stage: string }>
) => {
  const position = allPositions.find((pos) => pos.id === pipelinePositionId);
  if (!position) return "gray.500";
  return getBadgeColorByStage(position.stage);
};

export const getBadgeBgColorByPipelinePosition = (
  pipelinePositionId: string,
  allPositions: Array<{ id: string; stage: string }>
) => {
  const position = allPositions.find((pos) => pos.id === pipelinePositionId);
  if (!position) return "gray.100";
  return getBadgeBgColorByStage(position.stage);
};

export const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const formatDateInBrowserTimezone = (dateString: string): string => {
  if (!dateString) return "";
  return new Date(dateString)
    .toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
    .replace(",", " -");
};

export const getSystemPositionDescription = (positionId: string): string => {
  switch (positionId) {
    case "new":
      return "El prospecto inició una conversación con el agente, pero aún es gestionado por la IA.";
    case "delivered":
      return "El mensaje del agente fue entregado al prospecto en WhatsApp.";
    case "read":
      return "El prospecto leyó el mensaje enviado por el agente en WhatsApp.";
    case "answered":
      return "El prospecto respondió a la conversación en WhatsApp.";
    case "redirected":
      return "El prospecto fue transferido a un agente humano debido a la necesidad de un servicio especializado.";
    case "appointmentIntent":
      return "El prospecto expresó intención de agendar una cita o visitar la propiedad.";
    case "appointmentPending":
      return "El prospecto agendó una cita tentativa con la IA, pero requiere la validación de un agente humano.";
    case "appointmentConfirmed":
      return "El prospecto confirmó la cita tras ser contactado por un agente humano.";
    case "appointmentCancelled":
      return "El prospecto canceló la cita tras ser contactado por un agente humano.";
    case "closed":
      return "El prospecto finalizó la conversación y se registró un cierre.";
    case "inactive":
      return "El prospecto dejó de interactuar con la IA tras varios intentos de contacto y han pasado 5 días.";
    case "discarded":
      return "El prospecto fue descartado por el agente tras evaluar que no cumple con los criterios.";
    default:
      return "Estado desconocido del sistema.";
  }
};

export const getStageDescription = (stage: string): string => {
  switch (stage) {
    case "new":
      return "El prospecto está siendo gestionado por la IA. Es el estado inicial.";
    case "pending":
      return "El prospecto está siendo gestionado por un agente humano. Es un estado intermedio.";
    case "success":
      return "El prospecto logró un cierre exitoso. Es un estado final.";
    case "failure":
      return "El prospecto no logró cerrarse. Es un estado final.";
    case "discarded":
      return "El prospecto fue descartado por el agente. Es un estado final.";
    default:
      return "Estado desconocido del sistema.";
  }
};

// System positions map as fallback when position is not found in pipeline
export const SYSTEM_POSITIONS_DISPLAY_MAP: Record<string, string> = {
  new: "Nuevo",
  delivered: "Entregado",
  read: "Leído",
  answered: "Respondido",
  redirected: "Redirección humano",
  appointmentIntent: "Intención de cita",
  appointmentPending: "Pre-cita",
  appointmentConfirmed: "Cita confirmada",
  appointmentCancelled: "Cita cancelada",
  propertyInfoCollected: "Info recolectada",
  maintenanceRequested: "Solicitud de Mantenimiento",
  closed: "Negocio Cerrado",
  inactive: "Inactivo",
  discarded: "Descartado",
};
